import { useCallback } from 'react';
import {
  Button,
  IconStatusCorrect,
  Text,
} from '@adc/parallax-component-library';
import { Dialog } from '@tamagui/dialog';

import { ModalContent } from '~/components/ModalContent';
import { ModalOverlay } from '~/components/ModalOverlay';
import { useModal } from '~/features/modals/useModal';
import { buildTestId } from '~/utils/buildTestId';

type DepartmentAddedModalProps = {
  testID: string;
  departmentName: string;
  idnName?: string;
  handleAddDepartmentAdmin: () => Promise<void>;
  handleViewDepartmentProfile: () => Promise<void>;
};

export const DepartmentAddedModal = ({
  testID,
  departmentName,
  idnName,
  handleAddDepartmentAdmin,
  handleViewDepartmentProfile,
}: DepartmentAddedModalProps) => {
  const { closeModal } = useModal();

  const onClose = useCallback(() => {
    closeModal('DepartmentAdded');
  }, [closeModal]);

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
      }
    },
    [onClose],
  );

  return (
    <Dialog open onOpenChange={onOpenChange} modal>
      <Dialog.Portal testID={buildTestId({ testID, suffix: 'portal' })}>
        <ModalOverlay testID={buildTestId({ testID, suffix: 'overlay' })} />

        <ModalContent
          testID={testID}
          key="content"
          paddingBottom="$12"
          maxWidth={800}
          alignItems="center"
        >
          <IconStatusCorrect size="M" color="$support.positive.default" />
          <Text
            type="$h3.strong"
            testID={buildTestId({
              testID,
              suffix: 'title',
            })}
            paddingTop="$2"
          >
            Department Added
          </Text>

          <Text type="$h4.default" color="$text.60" paddingTop="$1">
            The department {departmentName} has been added
            {idnName ? `to ${idnName}` : ''}.
          </Text>

          <Button
            variant="secondary"
            label="Add Admin to Department"
            onPress={() => void handleAddDepartmentAdmin()}
            marginTop="$8"
            // @ts-expect-error Parallax doesn't type this prop
            minWidth={256}
            testID={buildTestId({
              testID,
              suffix: ['button', 'add', 'admin', 'department'],
            })}
          />
          <Button
            variant="secondary"
            label="View Department Profile"
            onPress={() => void handleViewDepartmentProfile()}
            marginTop="$4"
            // @ts-expect-error Parallax doesn't type this prop
            minWidth={256}
            testID={buildTestId({
              testID,
              suffix: ['button', 'department', 'profile'],
            })}
          />

          <Button
            variant="primary"
            label="Done"
            onPress={onClose}
            marginTop="$4"
            // @ts-expect-error Parallax doesn't type this prop
            minWidth={256}
            testID={buildTestId({
              testID,
              suffix: ['button', 'done'],
            })}
          />
        </ModalContent>
      </Dialog.Portal>
    </Dialog>
  );
};
